'use client'

import { useState } from 'react'
import { Button } from '@/ui/button'
import { revokeApiKey } from '../actions/api-keys'
import { formatDistanceToNow } from 'date-fns'
import { TrashIcon } from '@phosphor-icons/react'

interface ApiKeyDisplay {
  id: string
  name: string
  keyPrefix: string
  createdAt: string
  lastUsedAt: string | null
}

interface ApiKeyListProps {
  keys: ApiKeyDisplay[]
  onKeyRevoked: () => void
}

export function ApiKeyList({ keys, onKeyRevoked }: ApiKeyListProps) {
  const [revoking, setRevoking] = useState<string | null>(null)

  async function handleRevoke(id: string) {
    setRevoking(id)
    try {
      await revokeApiKey(id)
      onKeyRevoked()
    } catch (error) {
      console.error('Failed to revoke key:', error)
    } finally {
      setRevoking(null)
    }
  }

  if (keys.length === 0) {
    return (
      <div className="text-muted-foreground py-8 text-center text-sm">
        No API keys yet. Create one to use with Apple Shortcuts.
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {keys.map((key) => (
        <div
          key={key.id}
          className="flex items-center justify-between rounded-lg border p-4"
        >
          <div className="space-y-1">
            <div className="font-medium">{key.name}</div>
            <div className="text-muted-foreground font-mono text-sm">
              {key.keyPrefix}...
            </div>
            <div className="text-muted-foreground text-xs">
              Created{' '}
              {formatDistanceToNow(new Date(key.createdAt), {
                addSuffix: true,
              })}
              {key.lastUsedAt && (
                <>
                  {' '}
                  · Last used{' '}
                  {formatDistanceToNow(new Date(key.lastUsedAt), {
                    addSuffix: true,
                  })}
                </>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleRevoke(key.id)}
            disabled={revoking === key.id}
            className="text-destructive hover:text-destructive"
          >
            <TrashIcon className="h-4 w-4" />
          </Button>
        </div>
      ))}
    </div>
  )
}
